import React, { FC } from 'react'
import {
  Heading,
  HStack,
  StackDivider,
  StackProps,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import MapButton from '@/app/[locale]/(public)/propiedades/[slug]/_components/MapButton'
import DescriptionItem from '@/app/[locale]/(public)/propiedades/[slug]/_components/DescriptionItem'
import { formatNumber } from '@/utils/format-number'
import { RealEstateDTO } from '@/core/real-estate/typings'
import FilesButton from '@/app/[locale]/(public)/propiedades/[slug]/_components/FilesButton'
import SpaceItem from '@/app/[locale]/(public)/propiedades/[slug]/_components/SpaceItem'
import RealEstateMedias from '@/app/[locale]/(public)/propiedades/[slug]/_components/RealEstateMedias'
import { formatPriceString } from '@/utils/format-price-string'

interface RealEstateDetailsProps extends StackProps {
  data: RealEstateDTO
}

const RealEstateDetails: FC<RealEstateDetailsProps> = ({ data, ...props }) => {
  return (
    <VStack id="real-estate-details" w="full" {...props}>
      <RealEstateMedias medias={data.medias} />

      <VStack w="full" divider={<StackDivider />} flex={1} align="flex-start">
        <VStack spacing={4} align="flex-start" w="full">
          <HStack w="full">
            <FilesButton
              label="Brochures, Flyers y Videos"
              description="Descarga información acerca de la propiedad"
              medias={data.medias}
              repo={data.repo}
            />
            <MapButton />
          </HStack>
          <Text pt={4} color="primary" fontSize="lg" fontWeight="bold">
            {data.variant} en {data.additionalData.contractType?.toLowerCase()}
          </Text>
          <Heading pb={4} fontSize="2xl" as="h1">
            {data.name}
          </Heading>
        </VStack>

        {data.highlights?.length ? (
          <VStack py={4} spacing={4} align="flex-start" w="full">
            <Heading fontSize="lg" as="h4">
              Destacado
            </Heading>
            <UnorderedList py={2} spacing={4} w="full">
              {data.highlights?.map((h) => <DescriptionItem key={h} title="" content={h} />)}
            </UnorderedList>
          </VStack>
        ) : null}

        <VStack py={4} spacing={4} align="flex-start" w="full">
          <Heading fontSize="xl" as="h4">
            Características
          </Heading>
          <UnorderedList py={2} spacing={4} textDecor={'none'} w="full">
            <DescriptionItem title="Tipo" content={data.variant} />
            <DescriptionItem title="Submercado" content={data.additionalData.subMarket} />
            <DescriptionItem title="Contrato" content={data.additionalData.contractType} />
            <DescriptionItem title="Piso" content={data.additionalData.floor} />
            <DescriptionItem title="Cajones" content={data.additionalData.parkingSlots} />
            <DescriptionItem title="Clase" content={data.additionalData.realEstateClass} />
          </UnorderedList>
        </VStack>

        <VStack py={4} spacing={4} align="flex-start" w="full">
          <Heading fontSize="xl" as="h4">
            Precio
          </Heading>
          {!data.price?.rent?.price && !data.price?.sale?.price ? (
            <VStack flex={1}>
              <Text fontSize="lg">Contactar al broker</Text>
            </VStack>
          ) : (
            <VStack w="full" py={2}>
              <UnorderedList spacing={4} textDecor={'none'} w="full">
                {data.price.rent?.price ? (
                  <DescriptionItem
                    title="Renta"
                    content={formatPriceString(formatNumber(data.price.rent.price), {
                      label: data.price.rent.label,
                      currency: data.price.rent.currency,
                      showPrefix: true
                    })}
                  />
                ) : null}

                {data.price.sale?.price ? (
                  <DescriptionItem
                    title="Venta"
                    content={formatPriceString(formatNumber(data.price.sale.price), {
                      label: data.price.sale.label,
                      currency: data.price.sale.currency,
                      showPrefix: true
                    })}
                  />
                ) : null}

                {data.price.maintenanceAmount ? (
                  <DescriptionItem title="Mantenimiento" content={data.price.maintenanceAmount} />
                ) : null}
              </UnorderedList>
              <Text px={2} textAlign="left" w="full" fontStyle="italic">
                Disponibilidad y precios sujetos a cambios
              </Text>
            </VStack>
          )}
        </VStack>

        {data.size.rentableM2 ||
        data.size.officeM2 ||
        data.size.plotM2 ||
        data.size.totalConstructionM2 ||
        data.size.warehouseM2 ? (
          <VStack py={4} spacing={4} align="flex-start" w="full">
            <Heading fontSize="lg" as="h4">
              Tamaño
            </Heading>
            <UnorderedList py={2} spacing={4} textDecor={'none'} w="full">
              <DescriptionItem
                title="Rentables"
                content={
                  data.size.rentableM2 ? `${formatNumber(data.size.rentableM2)} m2` : undefined
                }
              />
              <DescriptionItem
                title="Oficina"
                content={data.size.officeM2 ? `${formatNumber(data.size.officeM2)} m2` : undefined}
              />
              <DescriptionItem
                title="Terreno"
                content={data.size.plotM2 ? `${formatNumber(data.size.plotM2)} m2` : undefined}
              />
              <DescriptionItem
                title="Construcción"
                content={
                  data.size.totalConstructionM2
                    ? `${formatNumber(data.size.totalConstructionM2)} m2`
                    : undefined
                }
              />
              <DescriptionItem
                title="Nave"
                content={
                  data.size.warehouseM2 ? `${formatNumber(data.size.warehouseM2)} m2` : undefined
                }
              />
            </UnorderedList>
          </VStack>
        ) : null}

        {data.spaces?.length ? (
          <VStack py={4} spacing={4} align="flex-start" w="full">
            <Heading fontSize="xl" as="h4">
              Espacios disponibles
            </Heading>
            {data.spaces.map((s) => (
              <SpaceItem key={s.id} space={s} />
            ))}
          </VStack>
        ) : null}
      </VStack>
    </VStack>
  )
}

export default RealEstateDetails
